import { useState, useEffect } from "react";
import { API } from "../../../cache";
import { SideDrawerShell } from "../../common/components";
import RefEntityAutocomplete from "./RefEntityAutocomplete";
import "../../../DailyTimesheetView.css";
import "../../../V2SideDrawer.css";
import "../styles/TimesheetEntryDrawer.css";

const STATUS_OPTS = ["Draft", "Submitted", "Approved"];

const STATUS_STYLE = {
  Draft:     { bg: "#fff8e1", color: "#f57f17" },
  Submitted: { bg: "#e3f2fd", color: "#1565c0" },
  Approved:  { bg: "#e8f5e9", color: "#2e7d32" },
};

function DrwField({ label, req, children, err }) {
  return (
    <div className="dts-drw-field">
      <label className={`v2drw-form-label${req ? " dts-drw-label--req" : ""}`}>{label}</label>
      {children}
      {err && <div className="dts-drw-err">{err}</div>}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtTime(h, m) {
  const hrs  = h || 0;
  const mins = m || 0;
  if (hrs === 0 && mins === 0) return "—";
  if (hrs === 0)  return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
}

function validate(form, requiresDefectEpic) {
  const errors = {};
  if (!form.ref_id)        errors.ref     = "Select a reference entity";
  if (!form.title?.trim()) errors.title   = "Required";
  const h = Number(form.hours_spent);
  const m = Number(form.minutes_spent);
  if (isNaN(h) || h < 0 || h > 8)  errors.hours   = "Must be 0–8";
  if (isNaN(m) || m < 0 || m > 59) errors.minutes = "Must be 0–59";
  // No timesheet entry against a defect is allowed without an epic — not
  // even Draft. Mirrors the backend's _apply_defect_epic_requirement, which
  // now rejects every status, not just Submit/Approve.
  if (requiresDefectEpic && !form.epic_id) errors.epic_id = "Pick an epic — required to log time against this defect";
  return errors;
}

export default function TimesheetEditDrawer({
  open,
  reportId,
  entities,
  entitiesLoading,
  showToast,
  onClose,
  onSaved,
}) {
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({});
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  // Defect has no epic yet — needs one attached via this edit (see
  // _apply_defect_epic_requirement on the backend). Only fetched while
  // actively editing a ref_type='defect' entry; null once the defect
  // already has its own epic.
  const [defectEpicOptions, setDefectEpicOptions] = useState(null);

  useEffect(() => {
    if (!editing || form.ref_type !== "defect" || !form.ref_id) {
      setDefectEpicOptions(null);
      return;
    }
    let cancelled = false;
    fetch(`${API}/defects/${form.ref_id}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(defect => {
        if (cancelled || !defect) return;
        if (defect.epic_id) {
          setDefectEpicOptions(null);
          return;
        }
        if (!defect.project_id) return;
        return fetch(`${API}/epics?project_id=${defect.project_id}&include_closed=false`, { credentials: "include" })
          .then(r => r.ok ? r.json() : [])
          .then(data => {
            if (cancelled) return;
            const list = Array.isArray(data) ? data : (data?.items || []);
            setDefectEpicOptions(list);
          });
      })
      .catch(() => { if (!cancelled) setDefectEpicOptions(null); });
    return () => { cancelled = true; };
  }, [editing, form.ref_type, form.ref_id]);

  useEffect(() => {
    if (!open || !reportId) {
      setReport(null);
      setEditing(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setEditing(false);
    setReport(null);
    fetch(`${API}/daily-reports/${reportId}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (!cancelled) { setReport(data); setLoading(false); } })
      .catch(() => {
        if (!cancelled) { showToast("Failed to load report", "warn"); setLoading(false); onClose(); }
      });
    return () => { cancelled = true; };
  }, [open, reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setForm({
      report_date:   report.report_date   ?? "",
      ref_type:      report.ref_type      ?? "",
      ref_id:        report.ref_id        ?? "",
      title:         report.title         ?? "",
      overall_notes: report.overall_notes ?? "",
      hours_spent:   report.hours_spent   ?? 0,
      minutes_spent: report.minutes_spent ?? 0,
      status:        report.status        ?? "Draft",
      epic_id:       "",
    });
    setErrors({});
    setEditing(true);
  };

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });
  };

  const handleSave = async () => {
    const errs = validate(form, !!defectEpicOptions);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      showToast("Please fill in the required fields", "warn");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        hours_spent:   Number(form.hours_spent),
        minutes_spent: Number(form.minutes_spent),
      };
      const r = await fetch(`${API}/daily-reports/${report.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || "Failed to update report");
      showToast("Report updated");
      if (data?.budget_warning) window.alert(data.budget_warning);
      onSaved?.({ ...report, ...payload });
      onClose();
    } catch (e) {
      showToast(e?.message || "Failed to update report", "warn");
    }
    setSaving(false);
  };

  const handleSubmit = async () => {
    try {
      const r = await fetch(`${API}/daily-reports/${report.id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Submitted" }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || "Failed to submit report");
      // Backend decides the real final status (Approved if within the
      // epic's budget, Submitted otherwise) — don't assume "Submitted".
      const finalStatus = data?.status || "Submitted";
      showToast(finalStatus === "Approved" ? "Report submitted and auto-approved" : "Report submitted");
      if (data?.budget_warning) window.alert(data.budget_warning);
      const updated = { ...report, status: finalStatus };
      setReport(updated);
      onSaved?.(updated);
    } catch (e) {
      showToast(e?.message || "Failed to submit report", "warn");
    }
  };

  const viewFooter = (
    <>
      <div className="v2drw-foot-spacer" />
      {report?.status === "Draft" && (
        <button type="button" className="v2drw-btn-save" onClick={handleSubmit}>
          Submit
        </button>
      )}
      <button type="button" className="v2drw-btn-cancel" onClick={onClose}>
        Close
      </button>
    </>
  );

  const editFooter = (
    <>
      <div className="v2drw-foot-spacer" />
      <button type="button" className="v2drw-btn-cancel" onClick={() => setEditing(false)} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="v2drw-btn-save" onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </>
  );

  return (
    <SideDrawerShell
      open={open}
      subtitle="TIMESHEET"
      title={editing ? "Edit Report" : (loading ? "Loading…" : (report?.title || "Daily Report"))}
      onClose={onClose}
      footer={editing ? editFooter : viewFooter}
      width="min(520px, 100vw)"
    >
      {loading && <div className="dts-state">Loading…</div>}

      {!loading && report && !editing && (
        <div className="dts-drw-view">
          <div className="dts-drw-view-actions">
            <span
              className="dts-badge"
              style={{ background: STATUS_STYLE[report.status]?.bg, color: STATUS_STYLE[report.status]?.color }}
            >
              {report.status}
            </span>
            <button className="uv-btn uv-btn--edit" onClick={startEdit}>Edit</button>
          </div>

          <div className="dts-drw-view-meta">
            <div className="dts-drw-view-row">
              <span className="dts-drw-view-label">Date</span>
              <span className="dts-drw-view-val">{fmtDate(report.report_date)}</span>
            </div>
            <div className="dts-drw-view-row">
              <span className="dts-drw-view-label">Type</span>
              <span className="dts-drw-view-val">{report.ref_type || "—"}</span>
            </div>
            <div className="dts-drw-view-row">
              <span className="dts-drw-view-label">Time</span>
              <span className="dts-drw-view-val">{fmtTime(report.hours_spent, report.minutes_spent)}</span>
            </div>
            <div className="dts-drw-view-row">
              <span className="dts-drw-view-label">Submitted By</span>
              <span className="dts-drw-view-val">{report.user_display_name || report.user_name || "—"}</span>
            </div>
          </div>

          {report.overall_notes && (
            <div className="dts-drw-view-notes">{report.overall_notes}</div>
          )}
        </div>
      )}

      {!loading && report && editing && (
        <div className="dts-drw-form">
          <DrwField label="Date" req>
            <input
              type="date"
              className="v2drw-control"
              value={form.report_date}
              onChange={e => set("report_date", e.target.value)}
            />
          </DrwField>

          <DrwField label="Reference Entity" req err={errors.ref}>
            <RefEntityAutocomplete
              refId={form.ref_id}
              refType={form.ref_type}
              entities={entities}
              loading={entitiesLoading}
              required
              onChange={(id, type) => {
                setForm(f => ({ ...f, ref_id: id, ref_type: type, epic_id: "" }));
                setErrors(e => {
                  if (!e.ref) return e;
                  const next = { ...e };
                  delete next.ref;
                  return next;
                });
              }}
            />
          </DrwField>

          {defectEpicOptions && (
            <DrwField label="Epic" req err={errors.epic_id}>
              <select
                className={`v2drw-control${errors.epic_id ? " err" : ""}`}
                value={form.epic_id}
                onChange={e => set("epic_id", e.target.value)}
              >
                <option value="">This defect has no epic — pick one to log time against it</option>
                {defectEpicOptions.map(ep => (
                  <option key={ep.id} value={ep.id}>{ep.epic_name}</option>
                ))}
              </select>
            </DrwField>
          )}

          <DrwField label="Title" req err={errors.title}>
            <input
              type="text"
              className="v2drw-control"
              value={form.title}
              onChange={e => set("title", e.target.value)}
              autoFocus
            />
          </DrwField>

          <DrwField label="Overall Notes">
            <textarea
              className="v2drw-control"
              placeholder="Summary of today's work…"
              value={form.overall_notes}
              onChange={e => set("overall_notes", e.target.value)}
            />
          </DrwField>

          <div className="dts-drw-row">
            <DrwField label="Hours Spent" err={errors.hours}>
              <input
                type="number"
                className="v2drw-control"
                min={0}
                max={8}
                value={form.hours_spent}
                onChange={e => set("hours_spent", e.target.value)}
              />
            </DrwField>
            <DrwField label="Minutes Spent" err={errors.minutes}>
              <input
                type="number"
                className="v2drw-control"
                min={0}
                max={59}
                value={form.minutes_spent}
                onChange={e => set("minutes_spent", e.target.value)}
              />
            </DrwField>
          </div>

          <DrwField label="Status">
            <select
              className="v2drw-control"
              value={form.status}
              onChange={e => set("status", e.target.value)}
            >
              {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </DrwField>
        </div>
      )}
    </SideDrawerShell>
  );
}
